/**
 * The OOTD day contract as pure time maths, so the queue, the row countdowns
 * and the tests all agree on one definition of "today's queue".
 *
 * Outfits are generated around 3am IST for the following day, and a stylist
 * has until 11 PM IST that evening to action them. At 11 PM IST the backend
 * cutoff job (`schedule_day_ahead_cutoff`) ships every outfit still awaiting
 * review exactly as generated. Changing the deadline is a change to this file,
 * the backend cutoff hour, and the label in `CutoffCountdown`.
 */

export const IST_OFFSET_MINUTES = 330;
export const CUTOFF_HOUR_IST = 23;

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

/** The calendar date it currently is in IST. */
export function istDay(now: Date): string {
  return new Date(now.getTime() + IST_OFFSET_MINUTES * 60_000).toISOString().slice(0, 10);
}

/** The date the queue in front of a stylist right now ships for: tomorrow, IST. */
export function targetDateFor(now: Date): string {
  return new Date(Date.parse(`${istDay(now)}T00:00:00Z`) + DAY_MS).toISOString().slice(0, 10);
}

/** 11 PM IST on the evening before `targetDate`, as an absolute instant. */
export function cutoffInstant(targetDate: string): Date {
  const midnight = Date.parse(`${targetDate}T00:00:00Z`);
  return new Date(midnight - DAY_MS + CUTOFF_HOUR_IST * HOUR_MS - IST_OFFSET_MINUTES * 60_000);
}

export function millisecondsToCutoff(now: Date, targetDate: string): number {
  return cutoffInstant(targetDate).getTime() - now.getTime();
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function formatCountdown(milliseconds: number): string {
  if (milliseconds <= 0) return 'Deadline passed';
  const seconds = Math.floor(milliseconds / 1000);
  return `${Math.floor(seconds / 3600)}h ${pad(Math.floor((seconds % 3600) / 60))}m ${pad(seconds % 60)}s`;
}

/** The weekday the look is worn on - the key into the member's lifestyle answers. */
export function weekdayFor(targetDate: string): string {
  return new Date(`${targetDate}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    timeZone: 'UTC',
  });
}
